"use client";

import { createClient, type Session, type SupabaseClient } from "@supabase/supabase-js";
import {
  ChangeEvent,
  FormEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

type MainTab = "pairing" | "explore" | "home" | "cellar" | "my";
type ExploreSection = "categories" | "recommendations" | "awards" | "festivals";
type AuthMode = "signin" | "signup";

type AppConfig = {
  supabaseUrl?: string;
  supabasePublishableKey?: string;
};

type Liquor = {
  id: string;
  name_ko: string;
  name_en?: string | null;
  abv?: number | null;
  liquor_type?: string | null;
  origin_region?: string | null;
  image_url?: string | null;
  average_rating?: number | null;
  review_count?: number | null;
  is_featured?: boolean | null;
  awards_count?: number | null;
  latest_award_title?: string | null;
};

type FeedPost = {
  id: string;
  author_display_name?: string | null;
  author_username?: string | null;
  author_avatar_url?: string | null;
  content?: string | null;
  created_at?: string | null;
  image_urls?: string[] | null;
  like_count?: number | null;
  comment_count?: number | null;
  is_liked?: boolean | null;
  is_bookmarked?: boolean | null;
  liquor_name_ko?: string | null;
};

type Festival = {
  id: string;
  title: string;
  city?: string | null;
  venue?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
  poster_url?: string | null;
};

type CellarItem = {
  id: string;
  liquor_id?: string | null;
  liquor_name_ko?: string | null;
  liquor_type?: string | null;
  image_url?: string | null;
  status?: string | null;
  purchased_at?: string | null;
};

type FoodAnalysis = {
  food_name: string;
  ingredients: string[];
  cooking_method: string;
  flavor_profile: {
    spiciness: number;
    richness: number;
    sweetness: number;
    acidity: number;
    saltiness: number;
  };
  confidence: number;
};

type PairingRecommendation = {
  liquor_id: string;
  rank: number;
  score: number;
  reason: string;
  caution?: string | null;
};

const TABS: { id: MainTab; label: string; icon: string }[] = [
  { id: "pairing", label: "페어링", icon: "✦" },
  { id: "explore", label: "탐색", icon: "⌕" },
  { id: "home", label: "홈", icon: "⌂" },
  { id: "cellar", label: "보관함", icon: "◇" },
  { id: "my", label: "MY", icon: "○" },
];

const EXPLORE_SECTIONS: { id: ExploreSection; label: string }[] = [
  { id: "categories", label: "카테고리" },
  { id: "recommendations", label: "추천" },
  { id: "awards", label: "어워드" },
  { id: "festivals", label: "축제" },
];

const SAMPLE_LIQUORS: Liquor[] = [
  {
    id: "sample-1",
    name_ko: "문배술 25",
    name_en: "Moonbaeju 25",
    abv: 25,
    liquor_type: "증류주",
    origin_region: "평양",
    average_rating: 4.8,
    review_count: 126,
    is_featured: true,
  },
  {
    id: "sample-2",
    name_ko: "한산소곡주",
    name_en: "Hansan Sogokju",
    abv: 16,
    liquor_type: "약주",
    origin_region: "충남 서천",
    average_rating: 4.7,
    review_count: 89,
    awards_count: 2,
    latest_award_title: "대한민국 주류대상",
  },
  {
    id: "sample-3",
    name_ko: "서울의 밤",
    name_en: "Seoul Night",
    abv: 25,
    liquor_type: "리큐르",
    origin_region: "서울",
    average_rating: 4.6,
    review_count: 203,
  },
  {
    id: "sample-4",
    name_ko: "복순도가 손막걸리",
    name_en: "Boksoondoga",
    abv: 6.5,
    liquor_type: "탁주",
    origin_region: "울산",
    average_rating: 4.9,
    review_count: 312,
    is_featured: true,
  },
];

const SAMPLE_POSTS: FeedPost[] = [
  {
    id: "sample-post-1",
    author_display_name: "주인 큐레이터",
    author_username: "juin",
    content:
      "맑은 증류주의 은은한 배 향과 담백한 흰살생선. 서로의 여운을 해치지 않는 오늘의 페어링이에요.",
    created_at: new Date().toISOString(),
    like_count: 48,
    comment_count: 7,
    liquor_name_ko: "문배술 25",
  },
  {
    id: "sample-post-2",
    author_display_name: "맛의 기록",
    author_username: "table_notes",
    content:
      "바삭하게 구운 전에는 산미가 또렷한 막걸리를. 느끼함은 덜고 곡물의 고소함은 길게 남습니다.",
    created_at: new Date(Date.now() - 86400000).toISOString(),
    like_count: 31,
    comment_count: 4,
    liquor_name_ko: "복순도가 손막걸리",
  },
];

const SAMPLE_FESTIVALS: Festival[] = [
  {
    id: "sample-festival-1",
    title: "우리술 대축제",
    city: "서울",
    venue: "양재 aT센터",
    starts_at: "2026-11-20T10:00:00+09:00",
  },
  {
    id: "sample-festival-2",
    title: "대한민국 와인축제",
    city: "영동",
    venue: "영동 레인보우 힐링관광지",
    starts_at: "2026-10-09T10:00:00+09:00",
  },
];

function normalizedEmail(username: string) {
  const value = username.trim().toLowerCase();
  return value.includes("@") ? value : `${value}@juin.local`;
}

function displayDate(value?: string | null) {
  if (!value) return "일정 준비 중";
  return new Intl.DateTimeFormat("ko-KR", {
    month: "long",
    day: "numeric",
    weekday: "short",
  }).format(new Date(value));
}

function relativeDate(value?: string | null) {
  if (!value) return "방금";
  const days = Math.floor((Date.now() - new Date(value).getTime()) / 86400000);
  if (days <= 0) return "오늘";
  if (days === 1) return "어제";
  return `${days}일 전`;
}

function initials(value?: string | null) {
  return (value || "J").trim().slice(0, 1).toUpperCase();
}

function BottleArtwork({ tone = 0, image, alt }: { tone?: number; image?: string | null; alt: string }) {
  if (image) {
    return (
      <div className="bottle-art photo">
        {/* Remote Supabase images are intentionally rendered without optimization. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={image} alt={alt} />
      </div>
    );
  }
  return (
    <div className={`bottle-art tone-${tone % 4}`} role="img" aria-label={`${alt} 병 이미지`}>
      <span className="bottle-silhouette">
        <span>{alt.slice(0, 2)}</span>
      </span>
    </div>
  );
}

function SectionHeader({
  eyebrow,
  title,
  action,
}: {
  eyebrow?: string;
  title: string;
  action?: ReactNode;
}) {
  return (
    <div className="section-header">
      <div>
        {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
        <h2>{title}</h2>
      </div>
      {action}
    </div>
  );
}

export default function JuInApp() {
  const [tab, setTab] = useState<MainTab>("home");
  const [exploreSection, setExploreSection] = useState<ExploreSection>("categories");
  const [client, setClient] = useState<SupabaseClient | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isSampleCatalog, setIsSampleCatalog] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [notice, setNotice] = useState("");
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<AuthMode>("signin");
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [liquors, setLiquors] = useState<Liquor[]>(SAMPLE_LIQUORS);
  const [posts, setPosts] = useState<FeedPost[]>(SAMPLE_POSTS);
  const [festivals, setFestivals] = useState<Festival[]>(SAMPLE_FESTIVALS);
  const [cellar, setCellar] = useState<CellarItem[]>([]);
  const [search, setSearch] = useState("");
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [analysis, setAnalysis] = useState<FoodAnalysis | null>(null);
  const [recommendations, setRecommendations] = useState<PairingRecommendation[]>([]);
  const [pairingBusy, setPairingBusy] = useState(false);
  const [profile, setProfile] = useState<{ display_name?: string; username?: string; bio?: string } | null>(
    null,
  );
  const fileRef = useRef<HTMLInputElement>(null);

  const loadPublicData = useCallback(async (supabase: SupabaseClient) => {
    const [liquorResult, festivalResult] = await Promise.all([
      supabase
        .from("explore_liquors")
        .select(
          "id,name_ko,name_en,abv,liquor_type,origin_region,image_url,average_rating,review_count,is_featured,awards_count,latest_award_title",
        )
        .limit(24),
      supabase
        .from("festival_events")
        .select("id,title,city,venue,starts_at,ends_at,poster_url")
        .eq("is_active", true)
        .order("starts_at", { ascending: true })
        .limit(12),
    ]);

    if (liquorResult.data?.length) {
      setLiquors(liquorResult.data as Liquor[]);
      setIsSampleCatalog(false);
    } else {
      setIsSampleCatalog(true);
    }
    if (festivalResult.data?.length) setFestivals(festivalResult.data as Festival[]);

    const feedResult = await supabase.rpc("feed_page", {
      p_page_size: 12,
      p_cursor_created_at: null,
      p_cursor_id: null,
    });
    if (Array.isArray(feedResult.data) && feedResult.data.length) {
      setPosts(feedResult.data as FeedPost[]);
    }
  }, []);

  const loadPrivateData = useCallback(async (supabase: SupabaseClient, activeSession: Session | null) => {
    if (!activeSession) {
      setProfile(null);
      setCellar([]);
      return;
    }
    const [profileResult, cellarResult] = await Promise.all([
      supabase
        .from("profiles")
        .select("display_name,username,bio")
        .eq("id", activeSession.user.id)
        .maybeSingle(),
      supabase.rpc("my_cellar_items"),
    ]);
    if (profileResult.data) setProfile(profileResult.data);
    if (Array.isArray(cellarResult.data)) setCellar(cellarResult.data as CellarItem[]);
  }, []);

  useEffect(() => {
    let mounted = true;
    fetch("/api/config")
      .then((response) => response.json() as Promise<AppConfig>)
      .then(async (config) => {
        if (!mounted || !config.supabaseUrl || !config.supabasePublishableKey) return;
        const supabase = createClient(config.supabaseUrl, config.supabasePublishableKey, {
          auth: { persistSession: true, detectSessionInUrl: true },
        });
        setClient(supabase);
        setIsConnected(true);
        const {
          data: { session: currentSession },
        } = await supabase.auth.getSession();
        if (!mounted) return;
        setSession(currentSession);
        await Promise.all([loadPublicData(supabase), loadPrivateData(supabase, currentSession)]);
        const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
          setSession(nextSession);
          void loadPrivateData(supabase, nextSession);
        });
        return () => data.subscription.unsubscribe();
      })
      .catch(() => setNotice("연결 없이 미리보기 데이터로 표시 중입니다."))
      .finally(() => {
        if (mounted) setIsLoading(false);
      });
    const timeout = window.setTimeout(() => setIsLoading(false), 900);
    return () => {
      mounted = false;
      window.clearTimeout(timeout);
    };
  }, [loadPrivateData, loadPublicData]);

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(""), 3200);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const currentName =
    profile?.display_name ||
    session?.user.user_metadata?.display_name ||
    session?.user.user_metadata?.username ||
    "게스트";

  const filteredLiquors = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return liquors;
    return liquors.filter((item) =>
      [item.name_ko, item.name_en, item.liquor_type, item.origin_region]
        .filter(Boolean)
        .some((value) => value!.toLowerCase().includes(query)),
    );
  }, [liquors, search]);

  async function submitAuth(event: FormEvent) {
    event.preventDefault();
    setAuthError("");
    if (!client) {
      setAuthError("현재는 UI 미리보기 모드입니다. 배포 환경 연결 후 로그인할 수 있어요.");
      return;
    }
    setAuthBusy(true);
    const email = normalizedEmail(username);
    const result =
      authMode === "signin"
        ? await client.auth.signInWithPassword({ email, password })
        : await client.auth.signUp({
            email,
            password,
            options: {
              data: {
                username: username.trim().toLowerCase(),
                display_name: displayName.trim() || username.trim(),
              },
            },
          });
    setAuthBusy(false);
    if (result.error) {
      setAuthError(result.error.message);
      return;
    }
    setAuthOpen(false);
    setNotice(authMode === "signin" ? "다시 만나 반가워요." : "주인에 오신 것을 환영해요.");
  }

  function requireSession(message: string) {
    if (session) return true;
    setNotice(message);
    setAuthOpen(true);
    return false;
  }

  async function togglePost(post: FeedPost, action: "like" | "bookmark") {
    if (!client || !requireSession("로그인하면 마음과 저장을 남길 수 있어요.")) return;
    const table = action === "like" ? "post_likes" : "post_bookmarks";
    const active = action === "like" ? post.is_liked : post.is_bookmarked;
    const query = client.from(table);
    const result = active
      ? await query.delete().eq("post_id", post.id).eq("user_id", session!.user.id)
      : await query.insert({ post_id: post.id, user_id: session!.user.id });
    if (result.error) {
      setNotice("잠시 후 다시 시도해 주세요.");
      return;
    }
    setPosts((items) =>
      items.map((item) =>
        item.id === post.id
          ? {
              ...item,
              ...(action === "like"
                ? { is_liked: !active, like_count: Math.max(0, (item.like_count || 0) + (active ? -1 : 1)) }
                : { is_bookmarked: !active }),
            }
          : item,
      ),
    );
  }

  function choosePhoto(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setNotice("사진은 8MB 이하로 선택해 주세요.");
      return;
    }
    if (photoUrl) URL.revokeObjectURL(photoUrl);
    setPhotoFile(file);
    setPhotoUrl(URL.createObjectURL(file));
    setAnalysis(null);
    setRecommendations([]);
  }

  async function analyzePhoto() {
    if (!photoFile || !client || !requireSession("사진 분석은 로그인 후 이용할 수 있어요.")) return;
    setPairingBusy(true);
    setAnalysis(null);
    const extension = photoFile.type === "image/png" ? "png" : "jpg";
    const path = `pairing/${session!.user.id}/${crypto.randomUUID()}.${extension}`;
    const upload = await client.storage.from("pairing-images").upload(path, photoFile, {
      cacheControl: "3600",
      contentType: photoFile.type || "image/jpeg",
    });
    if (upload.error) {
      setPairingBusy(false);
      setNotice("사진 업로드에 실패했어요.");
      return;
    }
    const response = await client.functions.invoke<FoodAnalysis>("analyze-food-image", {
      body: { bucket: "pairing-images", path },
    });
    await client.storage.from("pairing-images").remove([path]);
    if (response.error || !response.data) {
      setPairingBusy(false);
      setNotice("음식 분석을 마치지 못했어요. 다른 사진으로 다시 시도해 주세요.");
      return;
    }
    setAnalysis(response.data);
    const recommendationResult = await client.functions.invoke<{
      confidence: number;
      recommendations: PairingRecommendation[];
    }>("generate-pairing", {
      body: {
        food_name: response.data.food_name,
        ingredients: response.data.ingredients,
        cooking_method: response.data.cooking_method,
        flavor_profile: response.data.flavor_profile,
      },
    });
    if (recommendationResult.data?.recommendations?.length) {
      setRecommendations(
        recommendationResult.data.recommendations.sort((a, b) => a.rank - b.rank),
      );
    } else {
      setNotice("음식 분석은 완료했지만 연결할 술 카탈로그가 아직 부족해요.");
    }
    setPairingBusy(false);
  }

  async function saveContent(kind: "liquor" | "festival", id: string) {
    if (!client || !requireSession("로그인하면 마음에 드는 정보를 저장할 수 있어요.")) return;
    if (id.startsWith("sample-")) {
      setNotice("샘플 콘텐츠는 저장되지 않아요. 실제 카탈로그 공개를 기다려 주세요.");
      return;
    }
    const result =
      kind === "liquor"
        ? await client
            .from("liquor_wishlists")
            .upsert(
              { user_id: session!.user.id, liquor_id: id },
              { onConflict: "user_id,liquor_id" },
            )
        : await client
            .from("festival_bookmarks")
            .upsert(
              { user_id: session!.user.id, event_id: id },
              { onConflict: "user_id,event_id" },
            );
    setNotice(result.error ? "저장하지 못했어요. 잠시 후 다시 시도해 주세요." : "보관함에 저장했어요.");
  }

  async function signOut() {
    if (!client) return;
    await client.auth.signOut();
    setTab("home");
    setNotice("안전하게 로그아웃했어요.");
  }

  return (
    <main className="site-shell">
      <aside className="brand-rail" aria-label="JuIn 소개">
        <div className="brand-lockup">
          <span className="brand-mark">J</span>
          <span>JuIn</span>
        </div>
        <div className="rail-copy">
          <p className="eyebrow">Taste, connected.</p>
          <h1>취향을 발견하고,<br />함께 나누는 술 생활.</h1>
          <p>
            오늘의 한 잔부터 음식 페어링까지. 술을 더 잘 즐기는 사람들의 감각적인 커뮤니티.
          </p>
        </div>
        <div className="rail-features">
          <span><b>01</b> 나만의 취향 탐색</span>
          <span><b>02</b> Pairing AI</span>
          <span><b>03</b> 함께 쓰는 테이스팅 노트</span>
        </div>
        <p className="rail-foot">JuIn · 주류 소셜 & 푸드 페어링</p>
      </aside>

      <section className="app-stage" aria-label="JuIn 웹 앱">
        <div className="device-frame">
          <header className="top-bar">
            <button className="wordmark" onClick={() => setTab("home")} aria-label="홈으로">
              JuIn
            </button>
            <div className="top-actions">
              {!isConnected || isSampleCatalog ? <span className="preview-pill">SAMPLE</span> : null}
              <button className="icon-button" aria-label="알림" onClick={() => setNotice("새 알림이 없어요.")}>
                ◌
              </button>
              {session ? (
                <button className="avatar-button" onClick={() => setTab("my")} aria-label="내 프로필">
                  {initials(currentName)}
                </button>
              ) : (
                <button className="text-button" onClick={() => setAuthOpen(true)}>
                  로그인
                </button>
              )}
            </div>
          </header>

          <div className="view-scroll">
            {isLoading ? (
              <LoadingView />
            ) : (
              <>
                {tab === "home" ? (
                  <HomeView
                    name={currentName}
                    posts={posts}
                    liquors={liquors}
                    onPairing={() => setTab("pairing")}
                    onExplore={() => setTab("explore")}
                    onPostAction={togglePost}
                  />
                ) : null}
                {tab === "pairing" ? (
                  <PairingView
                    photoUrl={photoUrl}
                    analysis={analysis}
                    recommendations={recommendations}
                    liquors={liquors}
                    busy={pairingBusy}
                    onChoose={() => fileRef.current?.click()}
                    onAnalyze={analyzePhoto}
                  />
                ) : null}
                {tab === "explore" ? (
                  <ExploreView
                    active={exploreSection}
                    onChange={setExploreSection}
                    liquors={filteredLiquors}
                    festivals={festivals}
                    search={search}
                    onSearch={setSearch}
                    onSave={saveContent}
                  />
                ) : null}
                {tab === "cellar" ? (
                  <CellarView
                    items={cellar}
                    signedIn={Boolean(session)}
                    onExplore={() => setTab("explore")}
                    onSignIn={() => setAuthOpen(true)}
                  />
                ) : null}
                {tab === "my" ? (
                  <MyView
                    signedIn={Boolean(session)}
                    name={currentName}
                    username={profile?.username || session?.user.user_metadata?.username}
                    bio={profile?.bio}
                    onSignIn={() => setAuthOpen(true)}
                    onSignOut={signOut}
                  />
                ) : null}
              </>
            )}
          </div>

          <nav className="tab-bar" aria-label="주요 메뉴">
            {TABS.map((item) => (
              <button
                key={item.id}
                className={tab === item.id ? "active" : ""}
                aria-current={tab === item.id ? "page" : undefined}
                onClick={() => setTab(item.id)}
              >
                <span className="tab-icon">{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </section>

      <input
        ref={fileRef}
        className="visually-hidden"
        type="file"
        accept="image/jpeg,image/png"
        onChange={choosePhoto}
      />

      {authOpen ? (
        <AuthSheet
          mode={authMode}
          username={username}
          password={password}
          displayName={displayName}
          busy={authBusy}
          error={authError}
          onMode={setAuthMode}
          onUsername={setUsername}
          onPassword={setPassword}
          onDisplayName={setDisplayName}
          onClose={() => {
            setAuthOpen(false);
            setAuthError("");
          }}
          onSubmit={submitAuth}
        />
      ) : null}
      {notice ? <div className="toast" role="status">{notice}</div> : null}
    </main>
  );
}

function LoadingView() {
  return (
    <div className="loading-view" aria-label="불러오는 중">
      <span className="loading-mark">J</span>
      <span className="loading-line" />
      <span className="loading-line short" />
    </div>
  );
}

function HomeView({
  name,
  posts,
  liquors,
  onPairing,
  onExplore,
  onPostAction,
}: {
  name: string;
  posts: FeedPost[];
  liquors: Liquor[];
  onPairing: () => void;
  onExplore: () => void;
  onPostAction: (post: FeedPost, action: "like" | "bookmark") => void;
}) {
  return (
    <div className="screen home-screen">
      <section className="welcome-block">
        <p className="eyebrow">오늘도 반가워요, {name}</p>
        <h1>당신의 취향에<br />딱 맞는 한 잔.</h1>
        <div className="hero-actions">
          <button className="primary-button" onClick={onPairing}>음식 사진으로 찾기</button>
          <button className="secondary-button" onClick={onExplore}>술 둘러보기</button>
        </div>
      </section>

      <section className="quick-card pairing-quick" onClick={onPairing}>
        <div>
          <span className="mini-icon">✦</span>
          <p className="eyebrow">PAIRING AI</p>
          <h2>오늘 메뉴에 어울리는<br />술을 바로 찾아보세요.</h2>
        </div>
        <span className="arrow">↗</span>
      </section>

      <section className="content-section">
        <SectionHeader eyebrow="CURATED FOR YOU" title="오늘의 발견" action={<button onClick={onExplore}>더 보기</button>} />
        <div className="horizontal-list">
          {liquors.slice(0, 4).map((liquor, index) => (
            <article className="discovery-card" key={liquor.id}>
              <BottleArtwork tone={index} image={liquor.image_url} alt={liquor.name_ko} />
              <div>
                <span>{liquor.liquor_type || "우리술"} · {liquor.abv ? `${liquor.abv}%` : "도수 정보"}</span>
                <h3>{liquor.name_ko}</h3>
                <p>★ {liquor.average_rating?.toFixed(1) || "새로움"}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="content-section feed-section">
        <SectionHeader eyebrow="JUIN SOCIAL" title="취향이 만나는 곳" />
        <div className="feed-list">
          {posts.map((post) => (
            <article className="post-card" key={post.id}>
              <div className="post-author">
                <span className="avatar">{initials(post.author_display_name)}</span>
                <div>
                  <strong>{post.author_display_name || "JuIn 사용자"}</strong>
                  <span>@{post.author_username || "juin"} · {relativeDate(post.created_at)}</span>
                </div>
                <button aria-label="게시물 더 보기">•••</button>
              </div>
              <p className="post-copy">{post.content}</p>
              {post.liquor_name_ko ? (
                <div className="tagged-liquor"><span className="tiny-bottle" />{post.liquor_name_ko}</div>
              ) : null}
              <div className="post-actions">
                <button className={post.is_liked ? "selected" : ""} onClick={() => onPostAction(post, "like")}>
                  {post.is_liked ? "♥" : "♡"} {post.like_count || 0}
                </button>
                <button>◯ {post.comment_count || 0}</button>
                <button className={post.is_bookmarked ? "selected save" : "save"} onClick={() => onPostAction(post, "bookmark")}>
                  {post.is_bookmarked ? "◆" : "◇"}
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

function PairingView({
  photoUrl,
  analysis,
  recommendations,
  liquors,
  busy,
  onChoose,
  onAnalyze,
}: {
  photoUrl: string | null;
  analysis: FoodAnalysis | null;
  recommendations: PairingRecommendation[];
  liquors: Liquor[];
  busy: boolean;
  onChoose: () => void;
  onAnalyze: () => void;
}) {
  const flavors = analysis
    ? [
        ["매운맛", analysis.flavor_profile.spiciness],
        ["풍부함", analysis.flavor_profile.richness],
        ["단맛", analysis.flavor_profile.sweetness],
        ["산미", analysis.flavor_profile.acidity],
      ]
    : [];
  return (
    <div className="screen pairing-screen">
      <header className="screen-intro">
        <p className="eyebrow">PAIRING AI</p>
        <h1>사진 한 장이면,<br />오늘의 한 잔이 보여요.</h1>
        <p>음식의 재료와 맛을 읽고 어울리는 술의 방향을 섬세하게 찾아드립니다.</p>
      </header>

      <button className={`photo-drop ${photoUrl ? "filled" : ""}`} onClick={onChoose}>
        {photoUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={photoUrl} alt="선택한 음식" />
        ) : (
          <>
            <span className="camera-glyph">⌑</span>
            <strong>음식 사진 선택</strong>
            <small>JPG 또는 PNG · 최대 8MB</small>
          </>
        )}
        <span className="photo-edit">{photoUrl ? "사진 바꾸기" : "사진 보관함 열기"}</span>
      </button>

      {photoUrl ? (
        <button className="primary-button full" onClick={onAnalyze} disabled={busy}>
          {busy ? "맛을 분석하고 있어요…" : "이 사진 분석하기"}
        </button>
      ) : null}

      {analysis ? (
        <section className="analysis-card">
          <p className="eyebrow">ANALYSIS · {analysis.confidence}%</p>
          <h2>{analysis.food_name}</h2>
          <p>{analysis.ingredients.join(", ")} · {analysis.cooking_method}</p>
          <div className="flavor-grid">
            {flavors.map(([label, value]) => (
              <div key={label}>
                <span>{label}</span>
                <div><i style={{ width: `${Math.min(100, Number(value) * 10)}%` }} /></div>
              </div>
            ))}
          </div>
          <div className="pairing-direction">
            <span>추천 방향</span>
            <strong>{analysis.flavor_profile.richness >= 6 ? "산미가 또렷한 술" : "향이 섬세한 술"}</strong>
            <p>보유한 술 카탈로그 안에서 맛의 균형을 가장 잘 이어주는 후보를 찾았습니다.</p>
          </div>
        </section>
      ) : null}

      {recommendations.length ? (
        <section className="recommendation-results">
          <SectionHeader eyebrow="YOUR PAIRING" title="오늘의 추천" />
          {recommendations.map((recommendation) => {
            const liquor = liquors.find((item) => item.id === recommendation.liquor_id);
            return (
              <article key={recommendation.liquor_id}>
                <span className="recommend-rank">{String(recommendation.rank).padStart(2, "0")}</span>
                <div>
                  <p>{liquor?.liquor_type || "PAIRING"} · {recommendation.score}점</p>
                  <h3>{liquor?.name_ko || "추천 주류"}</h3>
                  <span>{recommendation.reason}</span>
                  {recommendation.caution ? <small>{recommendation.caution}</small> : null}
                </div>
              </article>
            );
          })}
        </section>
      ) : null}

      <section className="pairing-how">
        <SectionHeader eyebrow="HOW IT WORKS" title="취향에 닿는 세 단계" />
        <ol>
          <li><span>01</span><div><strong>음식 사진을 선택해요</strong><p>전체 메뉴가 잘 보이는 밝은 사진이면 더 좋아요.</p></div></li>
          <li><span>02</span><div><strong>AI가 맛을 이해해요</strong><p>재료, 조리법, 풍미의 균형을 함께 살펴봅니다.</p></div></li>
          <li><span>03</span><div><strong>어울리는 한 잔을 만나요</strong><p>이유가 있는 추천으로 새로운 취향을 발견하세요.</p></div></li>
        </ol>
      </section>
    </div>
  );
}

function ExploreView({
  active,
  onChange,
  liquors,
  festivals,
  search,
  onSearch,
  onSave,
}: {
  active: ExploreSection;
  onChange: (value: ExploreSection) => void;
  liquors: Liquor[];
  festivals: Festival[];
  search: string;
  onSearch: (value: string) => void;
  onSave: (kind: "liquor" | "festival", id: string) => void;
}) {
  return (
    <div className="screen explore-screen">
      <header className="screen-intro compact">
        <p className="eyebrow">DISCOVER YOUR TASTE</p>
        <h1>탐색</h1>
      </header>
      <label className="search-field">
        <span>⌕</span>
        <input
          value={search}
          onChange={(event) => onSearch(event.target.value)}
          placeholder="이름, 종류, 지역으로 검색"
          aria-label="술 검색"
        />
        {search ? <button onClick={() => onSearch("")}>×</button> : null}
      </label>
      <div className="segment-control" role="tablist">
        {EXPLORE_SECTIONS.map((item) => (
          <button
            key={item.id}
            className={active === item.id ? "active" : ""}
            onClick={() => onChange(item.id)}
            role="tab"
            aria-selected={active === item.id}
          >
            {item.label}
          </button>
        ))}
      </div>

      {active === "festivals" ? (
        <section className="festival-list">
          <SectionHeader eyebrow="SAVE THE DATE" title="술로 만나는 계절" />
          {festivals.map((festival, index) => (
            <article className="festival-card" key={festival.id}>
              <div className={`festival-poster poster-${index % 3}`}>
                {festival.poster_url ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={festival.poster_url} alt="" />
                ) : (
                  <span>{String(index + 1).padStart(2, "0")}</span>
                )}
              </div>
              <div>
                <span>{displayDate(festival.starts_at)}</span>
                <h3>{festival.title}</h3>
                <p>{festival.city || "지역 안내 예정"} · {festival.venue || "장소 안내 예정"}</p>
                <button onClick={() => onSave("festival", festival.id)}>일정 저장</button>
              </div>
            </article>
          ))}
        </section>
      ) : (
        <section className="liquor-grid-wrap">
          <SectionHeader
            eyebrow={active === "awards" ? "AWARD-WINNING" : active === "recommendations" ? "JUIN PICKS" : "ALL CATEGORIES"}
            title={
              active === "awards" ? "인정받은 한 잔" : active === "recommendations" ? "취향을 넓히는 추천" : "어떤 술을 찾으세요?"
            }
          />
          {liquors.length ? (
            <div className="liquor-grid">
              {liquors
                .filter((item) => (active === "awards" ? Boolean(item.awards_count) : true))
                .map((liquor, index) => (
                  <article className="liquor-card" key={liquor.id}>
                    <BottleArtwork tone={index} image={liquor.image_url} alt={liquor.name_ko} />
                    <button className="save-circle" onClick={() => onSave("liquor", liquor.id)} aria-label={`${liquor.name_ko} 저장`}>◇</button>
                    <span>{liquor.liquor_type || "우리술"} · {liquor.origin_region || "대한민국"}</span>
                    <h3>{liquor.name_ko}</h3>
                    <p>{liquor.name_en || `${liquor.abv || "-"}% ABV`}</p>
                    <strong>★ {liquor.average_rating?.toFixed(1) || "NEW"} <small>({liquor.review_count || 0})</small></strong>
                  </article>
                ))}
            </div>
          ) : (
            <EmptyState title="아직 등록된 술이 없어요" copy="카탈로그가 채워지면 이곳에서 취향을 탐색할 수 있어요." />
          )}
        </section>
      )}
    </div>
  );
}

function CellarView({
  items,
  signedIn,
  onExplore,
  onSignIn,
}: {
  items: CellarItem[];
  signedIn: boolean;
  onExplore: () => void;
  onSignIn: () => void;
}) {
  return (
    <div className="screen cellar-screen">
      <header className="screen-intro compact">
        <p className="eyebrow">YOUR COLLECTION</p>
        <h1>보관함</h1>
        <p>기억하고 싶은 술과 다시 만나고 싶은 순간을 한곳에 모아두세요.</p>
      </header>
      {!signedIn ? (
        <EmptyState
          icon="◇"
          title="나만의 보관함을 시작해요"
          copy="로그인하면 저장한 술, 위시리스트, 테이스팅 기록이 모든 기기에서 이어집니다."
          action={<button className="primary-button" onClick={onSignIn}>로그인하기</button>}
        />
      ) : items.length ? (
        <div className="cellar-grid">
          {items.map((item, index) => (
            <article key={item.id}>
              <BottleArtwork tone={index} image={item.image_url} alt={item.liquor_name_ko || "보관한 술"} />
              <span>{item.status === "opened" ? "마시는 중" : "보관 중"}</span>
              <h3>{item.liquor_name_ko || "이름 없는 술"}</h3>
              <p>{item.liquor_type || "주류"}</p>
            </article>
          ))}
        </div>
      ) : (
        <EmptyState
          icon="◇"
          title="첫 번째 술을 담아보세요"
          copy="탐색에서 마음에 드는 술을 저장하면 이곳에서 다시 만날 수 있어요."
          action={<button className="secondary-button" onClick={onExplore}>술 둘러보기</button>}
        />
      )}
    </div>
  );
}

function MyView({
  signedIn,
  name,
  username,
  bio,
  onSignIn,
  onSignOut,
}: {
  signedIn: boolean;
  name: string;
  username?: string;
  bio?: string;
  onSignIn: () => void;
  onSignOut: () => void;
}) {
  if (!signedIn) {
    return (
      <div className="screen my-screen">
        <EmptyState
          icon="J"
          title="취향을 이어가세요"
          copy="한 번의 로그인으로 나의 기록, 저장한 술, 페어링 결과를 안전하게 불러옵니다."
          action={<button className="primary-button" onClick={onSignIn}>JuIn 시작하기</button>}
        />
      </div>
    );
  }
  return (
    <div className="screen my-screen">
      <section className="profile-head">
        <span className="profile-avatar">{initials(name)}</span>
        <p className="eyebrow">MY TASTE PROFILE</p>
        <h1>{name}</h1>
        <p>@{username || "juin"} · {bio || "좋아하는 한 잔을 기록하는 중"}</p>
        <button className="secondary-button">프로필 편집</button>
      </section>
      <section className="profile-stats">
        <div><strong>0</strong><span>테이스팅</span></div>
        <div><strong>0</strong><span>저장한 술</span></div>
        <div><strong>0</strong><span>페어링</span></div>
      </section>
      <section className="settings-list">
        {["계정 및 프로필", "알림", "개인정보 보호", "서비스 정보"].map((item) => (
          <button key={item}><span>{item}</span><b>›</b></button>
        ))}
        <button className="logout" onClick={onSignOut}><span>로그아웃</span></button>
      </section>
    </div>
  );
}

function EmptyState({
  icon = "○",
  title,
  copy,
  action,
}: {
  icon?: string;
  title: string;
  copy: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <span>{icon}</span>
      <h2>{title}</h2>
      <p>{copy}</p>
      {action}
    </div>
  );
}

function AuthSheet({
  mode,
  username,
  password,
  displayName,
  busy,
  error,
  onMode,
  onUsername,
  onPassword,
  onDisplayName,
  onClose,
  onSubmit,
}: {
  mode: AuthMode;
  username: string;
  password: string;
  displayName: string;
  busy: boolean;
  error: string;
  onMode: (value: AuthMode) => void;
  onUsername: (value: string) => void;
  onPassword: (value: string) => void;
  onDisplayName: (value: string) => void;
  onClose: () => void;
  onSubmit: (event: FormEvent) => void;
}) {
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="auth-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button className="sheet-close" onClick={onClose} aria-label="닫기">×</button>
        <span className="auth-mark">J</span>
        <p className="eyebrow">TASTE, CONNECTED.</p>
        <h2 id="auth-title">{mode === "signin" ? "다시 만나 반가워요." : "취향의 여정을 시작해요."}</h2>
        <p>{mode === "signin" ? "나의 기록과 페어링을 이어서 만나보세요." : "JuIn에서 좋아하는 맛을 발견하고 기록하세요."}</p>
        <div className="auth-switch">
          <button className={mode === "signin" ? "active" : ""} onClick={() => onMode("signin")}>로그인</button>
          <button className={mode === "signup" ? "active" : ""} onClick={() => onMode("signup")}>회원가입</button>
        </div>
        <form onSubmit={onSubmit}>
          {mode === "signup" ? (
            <label>
              <span>표시 이름</span>
              <input value={displayName} onChange={(event) => onDisplayName(event.target.value)} placeholder="어떻게 불러드릴까요?" required />
            </label>
          ) : null}
          <label>
            <span>아이디</span>
            <input value={username} onChange={(event) => onUsername(event.target.value)} placeholder="juin_user" autoCapitalize="none" required />
          </label>
          <label>
            <span>비밀번호</span>
            <input type="password" value={password} onChange={(event) => onPassword(event.target.value)} placeholder="8자 이상 입력" minLength={8} required />
          </label>
          {error ? <p className="auth-error">{error}</p> : null}
          <button className="primary-button full" disabled={busy}>
            {busy ? "잠시만 기다려 주세요…" : mode === "signin" ? "로그인" : "계정 만들기"}
          </button>
        </form>
        <small>계속하면 JuIn의 이용약관 및 개인정보 처리방침에 동의하게 됩니다.</small>
      </section>
    </div>
  );
}
